// Merge free-text quantities from several recipes for one grocery line: "2 medium" + "3 medium"
// → "5 medium"; amounts that can't be added safely ("to taste", "a pinch") are listed as-is.
const FRACTIONS: Record<string, number> = { '½': 0.5, '¼': 0.25, '¾': 0.75, '⅓': 1 / 3, '⅔': 2 / 3 };

interface Parsed { n: number; unit: string }

export function parseQuantity(q: string): Parsed | null {
  const m = /^\s*(\d+(?:\.\d+)?)?\s*([½¼¾⅓⅔])?\s*(?:\/\s*\d+)?\s+(.+?)\s*$/.exec(q);
  if (!m || (!m[1] && !m[2])) {
    const bare = /^\s*(\d+(?:\.\d+)?)\s*$/.exec(q); // "3" (count)
    return bare ? { n: parseFloat(bare[1]), unit: '' } : null;
  }
  const n = (m[1] ? parseFloat(m[1]) : 0) + (m[2] ? FRACTIONS[m[2]] : 0);
  return { n, unit: m[3].toLowerCase() };
}

export function formatAmount(n: number): string {
  const whole = Math.floor(n + 1e-9);
  const frac = n - whole;
  const sym = Math.abs(frac - 0.5) < 0.01 ? '½' : Math.abs(frac - 0.25) < 0.01 ? '¼' : Math.abs(frac - 0.75) < 0.01 ? '¾' : '';
  if (!sym && frac > 0.01) return String(Math.round(n * 100) / 100);
  return (whole ? String(whole) : '') + (whole && sym ? ' ' : '') + sym || '0';
}

export function mergeQuantities(qs: string[]): string {
  const sums = new Map<string, number>();
  const other: string[] = [];
  for (const raw of qs) {
    // Notes like "(for garnish)" or "(big size, sliced)" don't change how much to buy.
    const q = (raw || '').replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
    if (!q) continue;
    const p = parseQuantity(q);
    if (p) sums.set(p.unit, (sums.get(p.unit) ?? 0) + p.n);
    else if (!other.some((o) => o.toLowerCase() === q.toLowerCase())) other.push(q);
  }
  const added = [...sums].map(([unit, n]) => (unit ? `${formatAmount(n)} ${unit}` : formatAmount(n)));
  return [...added, ...other].join(' + ');
}
